import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { combineLatest } from "rxjs";
import { debounceTime } from "rxjs/operators";

import { useHookSubscribe } from "../../../react-rxjs";

import { MapDimensionType } from "../../models/MapDimension";
import { MapDataSource } from "../../models/MapDataSource";

import { useMapFavouriteName } from "../../hooks/useMapFavouriteName";
import { useMapStore } from "../../hooks/useMapStore";

import {
  useFavouriteItemsQuery,
  useDeleteFavouriteMutation,
  useAddFavouriteMutation,
  useUpdateFavouriteMutation,
} from "../../../favourites/api";
import { isEqual } from "../../../favourites/utils";
import { FavouriteMapItem, FavouriteMapItemDraft, FavouriteItemType } from "../../../favourites/types";

import {
  IFavouriteProviderContextValue,
  IFavouriteProviderContextActions,
  IFavouriteProviderContextState,
  IFavouriteProviderProps,
} from "./FavouriteProvider.types";

export function useFavouriteState(props: IFavouriteProviderProps): IFavouriteProviderContextValue {
  const { search } = useLocation();
  const hasFavouriteParam = new URLSearchParams(search).get("favourite") !== null;

  const { applySavedFavouriteByDefault = hasFavouriteParam } = props;

  const { data: favouriteItems = [], isLoading: isFavouriteItemsLoading } = useFavouriteItemsQuery();
  const { mutate: updateFavouriteMutation } = useUpdateFavouriteMutation();
  const { mutate: addFavouriteMutation } = useAddFavouriteMutation();
  const { mutate: deleteFavouriteMutation } = useDeleteFavouriteMutation();

  const favouriteName = useMapFavouriteName();

  const store = useMapStore();
  const loadingDataSources = useHookSubscribe<boolean>(true, store.loadingDataSources$);
  const loadingDimensions = useHookSubscribe<boolean>(true, store.loadingDimensions$);
  const dataSource = useHookSubscribe<null | MapDataSource>(null, store.dataSource$);
  const dimensions = useHookSubscribe([], store.dimensions$);

  const [dimansionValues, setDimensionValues] = useState<Record<string, unknown[]>>({});

  useEffect(() => {
    combineLatest(dimensions.map((dim) => store.dimensionsValues.get(dim.id)!))
      .pipe(debounceTime(300))
      .subscribe((values) => {
        setDimensionValues(
          dimensions.reduce((acc, dim, index) => {
            if (dim.type !== MapDimensionType.Calendar && dim.visible) {
              acc[`@${dim.hierarchy.split(".")[0]}`] = [String(values[index])];
            }

            return acc;
          }, {} as Record<string, string[]>)
        );
      });
  }, [dataSource, dimensions]);

  const currentFavouriteItem = useMemo<FavouriteMapItemDraft | undefined>(() => {
    if (
      !dataSource ||
      !favouriteName ||
      Object.values(dimansionValues).length === 0 ||
      Object.values(dimansionValues).some((v) => v[0] === null)
    ) {
      return undefined;
    }

    return {
      type: FavouriteItemType.Map,
      name: favouriteName,
      mark: {
        parameters: {
          "@data_source": [String(dataSource.id)],
          ...dimansionValues,
        },
      },
    };
  }, [dimansionValues, favouriteName, dataSource]);

  const savedFavouriteItem = useMemo<FavouriteMapItem | undefined>(() => {
    return favouriteItems.find((item) => item.type === FavouriteItemType.Map) as FavouriteMapItem;
  }, [favouriteItems]);

  const applyFunction = useCallback(
    (favouriteItem: FavouriteMapItem) => {
      store.dataSourceId$.next(favouriteItem.mark.parameters["@data_source"].map(Number)[0]);
      store.favouriteItem$.next(favouriteItem);
    },
    [store]
  );
  const addFavourite = useCallback(() => {
    if (currentFavouriteItem) {
      addFavouriteMutation(currentFavouriteItem);
    }
  }, [addFavouriteMutation, currentFavouriteItem]);

  const deleteFavourite = useCallback(() => {
    if (savedFavouriteItem) {
      deleteFavouriteMutation(savedFavouriteItem);
    }
  }, [deleteFavouriteMutation, savedFavouriteItem]);

  const updateFavourite = useCallback(() => {
    if (savedFavouriteItem && currentFavouriteItem) {
      updateFavouriteMutation({ oldFavouriteItem: savedFavouriteItem, newFavouriteItem: currentFavouriteItem });
    }
  }, [updateFavouriteMutation, savedFavouriteItem, currentFavouriteItem]);

  const cancelFavouriteChanges = useCallback(() => {
    if (savedFavouriteItem && applyFunction) {
      applyFunction(savedFavouriteItem);
    }
  }, [savedFavouriteItem, applyFunction]);

  useEffect(() => {
    // reset favourite selection and fall back to default logic after changing data source
    const storeFavouriteItemDataSource = store.favouriteItem$.value?.mark.parameters?.["@data_source"]?.map(Number)[0];
    if (applySavedFavouriteByDefault && storeFavouriteItemDataSource !== dataSource?.id && store.favouriteItem$.value !== undefined) {
      store.favouriteItem$.next(undefined);
    }
  }, [dataSource, store, applySavedFavouriteByDefault]);

  useEffect(() => {
    if (savedFavouriteItem && applyFunction && applySavedFavouriteByDefault) {
      applyFunction(savedFavouriteItem);
    }
  }, [savedFavouriteItem, applyFunction, applySavedFavouriteByDefault]);

  const state = useMemo<IFavouriteProviderContextState>(
    () => ({
      currentFavouriteItem,
      savedFavouriteItem,
      isFavouriteLoading: isFavouriteItemsLoading || loadingDataSources || loadingDimensions,
      isFavouriteDisabled: !currentFavouriteItem,
      isFavouriteChanged: !!savedFavouriteItem && !!currentFavouriteItem && !isEqual(savedFavouriteItem, currentFavouriteItem),
    }),
    [savedFavouriteItem, isFavouriteItemsLoading, currentFavouriteItem, loadingDataSources, loadingDimensions]
  );

  const actions = useMemo<IFavouriteProviderContextActions>(
    () => ({
      addFavourite,
      deleteFavourite,
      updateFavourite,
      cancelFavouriteChanges,
    }),
    [addFavourite, deleteFavourite, updateFavourite, cancelFavouriteChanges]
  );

  const value = useMemo(() => ({ state, actions }), [actions, state]);

  return value;
}
